import type { SyntaxNode } from 'web-tree-sitter';
import type { SymbolImportBinding } from '../../types.js';
import { pushBinding } from './shared.js';

function identifierFromUseTree(node: SyntaxNode): SyntaxNode | null {
  if (node.type === 'identifier' || node.type === 'type_identifier') return node;
  const nameNode = node.childForFieldName('name');
  if (nameNode) return nameNode;
  for (const child of node.namedChildren) {
    const hit = identifierFromUseTree(child);
    if (hit) return hit;
  }
  return null;
}

function useDeclarationIsPublic(node: SyntaxNode): boolean {
  return node.children.some((child) => child.type === 'visibility_modifier' && child.text === 'pub');
}

function collectUseTree(
  out: SymbolImportBinding[],
  filePath: string,
  workspaceFiles: ReadonlySet<string>,
  pathPrefix: string,
  node: SyntaxNode,
  importKind: SymbolImportBinding['importKind'] = 'named',
): void {
  if (node.type === 'use_as_clause') {
    const pathNode = node.childForFieldName('path');
    const aliasNode = node.childForFieldName('alias');
    if (!pathNode || !aliasNode) return;
    const sourceName = identifierFromUseTree(pathNode)?.text ?? pathNode.text.split('::').at(-1) ?? '';
    pushBinding(out, filePath, workspaceFiles, pathPrefix, aliasNode.text, sourceName, importKind, aliasNode);
    return;
  }

  if (node.type === 'identifier' || node.type === 'type_identifier') {
    const sourceName = node.text;
    pushBinding(out, filePath, workspaceFiles, pathPrefix, sourceName, sourceName, importKind, node);
    return;
  }

  if (node.type === 'scoped_identifier') {
    const nameNode = node.childForFieldName('name');
    if (!nameNode) return;
    const sourceName = nameNode.text;
    pushBinding(out, filePath, workspaceFiles, pathPrefix, sourceName, sourceName, importKind, nameNode);
    return;
  }

  if (node.type === 'use_list') {
    for (const child of node.namedChildren) {
      collectUseTree(out, filePath, workspaceFiles, pathPrefix, child, importKind);
    }
    return;
  }

  if (node.type === 'scoped_use_list') {
    const pathNode = node.childForFieldName('path');
    const nextPrefix = pathNode ? `${pathPrefix}${pathNode.text}::` : pathPrefix;
    for (const child of node.namedChildren) {
      if (child === pathNode) continue;
      collectUseTree(out, filePath, workspaceFiles, nextPrefix, child, importKind);
    }
    return;
  }

  for (const child of node.namedChildren) {
    collectUseTree(out, filePath, workspaceFiles, pathPrefix, child, importKind);
  }
}

export function extractRustImportBindings(
  root: SyntaxNode,
  filePath: string,
  workspaceFiles: ReadonlySet<string>,
): SymbolImportBinding[] {
  const out: SymbolImportBinding[] = [];
  for (const node of root.descendantsOfType('use_declaration')) {
    const argument = node.childForFieldName('argument');
    if (!argument) continue;
    const importKind = useDeclarationIsPublic(node) ? 'reexport' : 'named';
    collectUseTree(out, filePath, workspaceFiles, '', argument, importKind);
  }
  return out;
}
